import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, payments as paymentsApi, guestPhotos as guestPhotosApi, uploadApi } from '../services/api';
import { formatPrice, formatDateRange, formatPolicy } from '../utils/formatters';
import { calculateCancellationRefund } from '../utils/timeDecay';

function daysUntil(dateStr) {
  const ms = new Date(dateStr).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

// ── Inline photo review form for a past stay ─────────────────────────────────
function PhotoReviewForm({ propertyId, onDone }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [status, setStatus] = useState('idle'); // idle | uploading | done | error

  async function submit(e) {
    e.preventDefault();
    if (!file) return;
    setStatus('uploading');
    try {
      const uploadRes = await uploadApi.images([file]);
      const url = uploadRes.data.urls[0];
      await guestPhotosApi.upload(propertyId, { url, category: 'Room', caption });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'done') {
    return <p style={{ fontSize: 'var(--text-sm)', color: 'var(--success)', margin: '0.5rem 0 0' }}>Photo submitted — thanks for sharing!</p>;
  }

  return (
    <form onSubmit={submit} style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 'var(--radius-sm)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => setFile(e.target.files[0] || null)} style={{ fontSize: 'var(--text-sm)' }} />
      <input type="text" placeholder="Caption (optional)" maxLength={255} value={caption} onChange={e => setCaption(e.target.value)}
        style={{ fontSize: 'var(--text-sm)', padding: '0.4rem 0.6rem' }} />
      {status === 'error' && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)', margin: 0 }}>Upload failed. Please try again.</p>}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={!file || status === 'uploading'}>
          {status === 'uploading' ? 'Uploading…' : 'Submit Photo'}
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

// ── Shared "My Stays" tab content ─────────────────────────────────────────────
// Upcoming stays with a check-in countdown + past stays with an optional
// photo review. Used by both the buyer and owner dashboards, since owner
// accounts can also book stays as a buyer.
export default function MyStays() {
  const { data: txData, loading: txLoading, refetch: refetchTx } = useApi('/payments/transactions');
  const transactions = txData?.transactions || [];

  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError] = useState('');
  const [reviewingId, setReviewingId] = useState(null);

  const upcoming = transactions.filter(t => t.status === 'captured' && new Date(t.check_in_date) > new Date());
  const past = transactions.filter(t => t.status === 'paid_out' || new Date(t.check_in_date) <= new Date());

  async function cancelBooking(transactionId, stayDate) {
    const { policy, buyerRefund } = calculateCancellationRefund(0, stayDate);
    const confirm = window.confirm(
      `Cancel this booking?\n\nPolicy: ${formatPolicy(policy)}${buyerRefund === 0 ? '\n\nYou will not receive a refund.' : ''}`
    );
    if (!confirm) return;

    setCancellingId(transactionId);
    setCancelError('');
    try {
      await paymentsApi.cancel(transactionId);
      refetchTx();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Cancellation failed. Please try again.');
    } finally {
      setCancellingId(null);
    }
  }

  if (txLoading) return <p style={{ color: 'var(--ink-muted)' }}>Loading stays…</p>;

  if (transactions.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--ink-muted)' }}>
        <p>No stays booked yet.</p>
        <Link to="/browse" className="btn btn-primary btn-sm" style={{ marginTop: '1rem' }}>Browse listings</Link>
      </div>
    );
  }

  return (
    <div>
      {cancelError && <div className="alert alert-error">{cancelError}</div>}

      {upcoming.length > 0 && (
        <>
          <h3 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Upcoming</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.75rem' }}>
            {upcoming.map(t => {
              const { policy } = calculateCancellationRefund(t.amount, t.check_in_date);
              const days = daysUntil(t.check_in_date);
              return (
                <div key={t.id} className="card" style={{ padding: '1.25rem', borderLeft: '3px solid var(--brand)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{t.property_name}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{formatDateRange(t.check_in_date, t.check_out_date)}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--brand)', fontWeight: 600, marginTop: '0.3rem' }}>
                        {days > 0 ? `${days} day${days !== 1 ? 's' : ''} until check-in` : 'Check-in today!'}
                      </div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-faint)', marginTop: '0.25rem' }}>
                        Cancellation: <strong>{formatPolicy(policy)}</strong> if cancelled now
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)' }}>{formatPrice(t.amount)}</div>
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ marginTop: '0.5rem' }}
                        disabled={cancellingId === t.id}
                        onClick={() => cancelBooking(t.id, t.check_in_date)}
                      >
                        {cancellingId === t.id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <h3 style={{ fontWeight: 600, marginBottom: '0.75rem' }}>Past Stays</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {past.map(t => (
              <div key={t.id} className="card" style={{ padding: '1.1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>{t.property_name}</div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>{formatDateRange(t.check_in_date, t.check_out_date)}</div>
                    <span className={`badge badge-${t.status}`} style={{ marginTop: '0.4rem', display: 'inline-block' }}>{t.status.replace('_', ' ')}</span>
                  </div>
                  {reviewingId !== t.id && (
                    <button className="btn btn-outline btn-sm" onClick={() => setReviewingId(t.id)}>
                      Leave a Photo Review
                    </button>
                  )}
                </div>
                {reviewingId === t.id && (
                  <PhotoReviewForm propertyId={t.property_id} onDone={() => setReviewingId(null)} />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
